import { graphQLClient, getOrgId } from '@devd-client/api';
import { gql } from 'graphql-request';
import type {
  ContributorTableFilter,
  ContributorsDataForTableResponse,
  ContributorTableResponse,
} from '../types/contributorTableTypes';

export const getContributorList = async (
  filter: ContributorTableFilter,
  teamIds: string[],
  startDate: string,
  endDate: string,
  sprintId: string,
  contributors: string[],
  pageNumber: number,
  pageSize: number
): Promise<ContributorsDataForTableResponse> =>
  await graphQLClient.request(
    gql`
      query GetContributorsDataForTable(
        $filter: Filter!
        $contributors: [String]!
        $pageNumber: Int
        $pageSize: Int
      ) {
        ContributorsDataForTable(
          filter: $filter
          contributors: $contributors
          pageNumber: $pageNumber
          pageSize: $pageSize
        ) {
          data {
            contributors {
              email
              name
              jobRole
              contributorAccountList {
                key {
                  orgId
                  type
                  account
                }
                emailId
                name
                suggestionScore
                suggestedEmail
              }
              suggestedContributors {
                key {
                  orgId
                  type
                  account
                }
                emailId
                name
                suggestionScore
                suggestedEmail
              }
              suggestedEmail
              suggestedScore
              isOfficialEmailPresent
            }
            teams
            languages
            knowledgeArea {
              name
              value
              enabled
            }
            teamAllocationSummary {
              name
              value
              enabled
            }
          }
          totalPages
          currentPage
          totalCount
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        startDate,
        endDate,
        sprintId,
        teamIds,
        projectsFilter: filter?.repoState ? filter?.repoState : [],
        searchTerm: filter?.searchTerm,
        jobRoles: filter?.jobRoleState,
        contributorFilters: {
          prog_lang: filter?.languageState,
          ...(filter?.activityFilter && {
            activeFilters: filter.activityFilter.activeFilters,
            inActiveFilters: filter.activityFilter.inActiveFilters,
            excluded: filter.activityFilter.excluded,
          }),
        },
      },
      contributors,
      pageNumber,
      pageSize,
    }
  );

export const getContributorTable = async (
  filter: ContributorTableFilter,
  teamIds: string[],
  startDate: string,
  endDate: string,
  sprintId: string,
  metric: string,
  contributors: string[],
  filteredContributors: string[],
  pageNumber: number,
  pageSize: number,
  orderBy: string
): Promise<ContributorTableResponse> => {
  return await graphQLClient.request(
    gql`
      query GetContributorTable(
        $filter: Filter!
        $metric: String!
        $contributors: [String]!
        $filteredContributors: [String]!
        $pageNumber: Int
        $pageSize: Int
        $orderBy: String!
      ) {
        ContributorTable(
          filter: $filter
          metric: $metric
          contributors: $contributors
          filteredContributors: $filteredContributors
          pageNumber: $pageNumber
          pageSize: $pageSize
          orderBy: $orderBy
        ) {
          metricName
          rows {
            contributor
            isError
            metricRowDataList {
              metric
              count
              unit
              improved
              percentage
              color
              totalCount
              data
              percentileData {
                percentChange
                improved
              }
            }
          }
          filteredContributors
          currentPage
          totalCount
          totalPages
        }
      }
    `,
    {
      filter: {
        orgId: getOrgId(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        startDate,
        endDate,
        sprintId,
        teamIds,
        showContributorPercentiles: filter?.showContributorPercentiles || false,
        projectsFilter: filter?.repoState ? filter?.repoState : [],
        searchTerm: filter?.searchTerm,
        jobRoles: filter?.jobRoleState,
        contributorFilters: {
          prog_lang: filter?.languageState,
          metricFilters: filter.metricFilters || [],
          ...(filter?.activityFilter && {
            activeFilters: filter.activityFilter.activeFilters,
            inActiveFilters: filter.activityFilter.inActiveFilters,
            excluded: filter.activityFilter.excluded,
          }),
        },
      },
      metric,
      contributors,
      filteredContributors,
      pageNumber,
      pageSize,
      orderBy,
    }
  );
};
